import React from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, Stack, Container } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useTranslation } from '@/i18n';

export const ErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();

  let errorMessage = t('common.somethingWentWrong');
  let errorStatus: number | string = 'Error';
  const errorRef = React.useMemo(
    () => `ERR-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
    []
  );

  if (isRouteErrorResponse(error)) {
    errorStatus = error.status;
    errorMessage = error.statusText || error.data?.message || errorMessage;
  } else if (error instanceof Error) {
    if (import.meta.env.DEV) {
      errorMessage = error.message;
    }
  }

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            textAlign: 'center',
            borderRadius: '10px',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '50%',
              backgroundColor: 'error.lighter',
              color: 'error.main',
              mb: 2.5,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 52 }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
            {t('common.somethingWentWrong')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 440, mx: 'auto' }}>
            {errorMessage}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.5,
              mb: 3.5,
              borderRadius: '4px',
              backgroundColor: 'action.hover',
              fontFamily: 'monospace',
              color: 'text.secondary',
            }}
          >
            {t('common.referenceId')}: {errorRef} ({errorStatus})
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="center"
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleReload}
              sx={{ fontWeight: 700, px: 2.5, minHeight: 40, borderRadius: '6px' }}
            >
              {t('common.tryAgain')}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              sx={{ fontWeight: 700, minHeight: 40, borderRadius: '6px' }}
            >
              {t('common.goBack')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<HomeOutlinedIcon />}
              onClick={handleGoHome}
              sx={{ fontWeight: 700, minHeight: 40, borderRadius: '6px' }}
            >
              {t('common.goToHome')}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};
